import { useContext, useState } from 'react';
import { GlobalContext } from '../../../context/GlobalContext';
import { unsafeAPI } from '../../../hooks/api';
import { Dialog } from '../../../components/Dialog';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { Button } from '../../../shadcn/Button';
import Notice from '../../../components/Notice';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import { Check, Copy, Hash, Pencil, RefreshCw, Save, X, XCircle } from 'lucide-react';
import { validate_bank_account_number } from '../../../utils/validate';
import { API_BASE, BANK_ACCOUNT_INTERPOLATION, DOCUMENT_TYPE_OPTIONS, COUNTRIES } from '../../../utils/constants';

interface PayoutDetailDialogProps {
	payout: { [key: string]: string | number };
	onClose: () => void;
	onActionComplete: () => void;
	liveBcvRate?: number;
}

const statusColors: Record<string, string> = {
	pending: 'bg-yellow-500/10 text-yellow-400',
	calculated: 'bg-blue-500/10 text-blue-400',
	reconciled: 'bg-blue-500/10 text-blue-400',
	processing: 'bg-amber-500/10 text-amber-400',
	completed: 'bg-emerald-500/10 text-emerald-400',
	failed: 'bg-red-500/10 text-red-400',
	cancelled: 'bg-neutral-500/10 text-neutral-400',
	retrying: 'bg-orange-500/10 text-orange-400',
};

const payoutMethodOptions = [
	{ label: 'BNC', value: 'bnc' },
	{ label: 'R4', value: 'r4' },
	{ label: 'Manual', value: 'manual' },
];

export default function PayoutDetailDialog({ payout, onClose, onActionComplete, liveBcvRate }: PayoutDetailDialogProps) {
	const { userData, internalCountry, CountrySettings } = useContext(GlobalContext);
	const country = internalCountry?.get() || '';
	const banks = CountrySettings?.banks?.toSorted() || [];
	const countryKey = (COUNTRIES as readonly string[]).includes(country) ? (country as (typeof COUNTRIES)[number]) : 'venezuela';
	const docTypeOptions = DOCUMENT_TYPE_OPTIONS[countryKey];

	const [showRetryConfirm, setShowRetryConfirm] = useState(false);
	const [showCancelConfirm, setShowCancelConfirm] = useState(false);
	const [cancelReason, setCancelReason] = useState('');
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
	const [copiedField, setCopiedField] = useState<string | null>(null);

	// Edit state
	const [editing, setEditing] = useState(false);
	const [editLoading, setEditLoading] = useState(false);
	const [editMethod, setEditMethod] = useState(String(payout['Método'] || '').toLowerCase());
	const [editBankAccount, setEditBankAccount] = useState(String(payout['Número de Cuenta'] || ''));
	const [editBankName, setEditBankName] = useState(String(payout['Banco'] || ''));
	const [editAccountHolder, setEditAccountHolder] = useState(String(payout['Titular'] || ''));
	const [editDocType, setEditDocType] = useState(String(payout['Tipo Documento'] || ''));
	const [editDocNumber, setEditDocNumber] = useState(String(payout['Número Documento'] || ''));

	const payoutId = String(payout['ID Payout'] || '');
	const status = String(payout['Estado'] || '').toLowerCase();
	const merchantEmail = String(payout['Email del Aliado'] || '');
	const payoutCountry = String(payout['País'] || '');
	const netAmount = payout['Monto Neto'];
	const currency = String(payout['Moneda'] || '');
	const method = String(payout['Método'] || '');
	const settlementDate = String(payout['Fecha de Venta'] || payout['Fecha Liquidación'] || '');
	const effectivePaymentDate = String(payout['Fecha de Pago Efectivo'] || '');
	const createdAt = String(payout['Fecha de Creación'] || '');
	const accountHolder = String(payout['Titular'] || '');
	const documentType = String(payout['Tipo Documento'] || '');
	const documentNumber = String(payout['Número Documento'] || '');
	const bankAccountNumber = String(payout['Número de Cuenta'] || '');
	const bankName = String(payout['Banco'] || '');
	const rowBcvRate = Number(payout['Tasa BCV']) || 0;
	const errorMessage = payout['Error'];
	const transferRef = payout['Referencia'];

	const canRetry = status === 'failed' || status === 'cancelled';
	const canCancel = status === 'pending' || status === 'calculated';
	const canEdit = status === 'pending' || status === 'failed' || status === 'retrying';

	const copyToClipboard = (text: string, fieldLabel: string) => {
		navigator.clipboard.writeText(text);
		setCopiedField(fieldLabel);
		setTimeout(() => setCopiedField(null), 1500);
	};

	const handleRetry = async (): Promise<true | string> => {
		const response = await unsafeAPI(
			'POST',
			`${API_BASE}/payments/merchant_payouts/retry/`,
			{ payout_id: payoutId },
			userData,
			internalCountry?.get()
		);
		if (response?.success) {
			setNotice({ message: 'Payout reintentado correctamente', variant: 'success' });
			onActionComplete();
			return true;
		}
		return response?.detail || 'Error al reintentar el payout';
	};

	const handleCancel = async (): Promise<true | string> => {
		if (!cancelReason.trim()) {
			return 'Debe ingresar una razón para cancelar';
		}
		const response = await unsafeAPI(
			'POST',
			`${API_BASE}/payments/merchant_payouts/cancel/`,
			{ payout_id: payoutId, reason: cancelReason },
			userData,
			internalCountry?.get()
		);
		if (response?.success) {
			setNotice({ message: 'Payout cancelado correctamente', variant: 'success' });
			onActionComplete();
			return true;
		}
		return response?.detail || 'Error al cancelar el payout';
	};

	const handleSaveEdit = async () => {
		setEditLoading(true);
		setNotice(null);

		const payload: Record<string, string> = { payout_id: payoutId };

		if (editMethod !== method.toLowerCase()) payload.payout_method = editMethod;
		if (editBankAccount !== bankAccountNumber) payload.bank_account_number = editBankAccount;
		if (editBankName !== bankName) payload.bank_name = editBankName;
		if (editAccountHolder !== accountHolder) payload.account_holder_name = editAccountHolder;
		if (editDocType !== documentType) payload.document_type = editDocType;
		if (editDocNumber !== documentNumber) payload.document_number = editDocNumber;

		// Nothing changed
		if (Object.keys(payload).length === 1) {
			setEditing(false);
			setEditLoading(false);
			return;
		}

		try {
			const response = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/merchant_payouts/update/`,
				payload,
				userData,
				internalCountry?.get()
			);

			if (response?.success) {
				setNotice({ message: 'Payout actualizado correctamente', variant: 'success' });
				setEditing(false);
				onActionComplete();
			} else {
				setNotice({ message: response?.detail || 'Error al actualizar el payout', variant: 'error' });
			}
		} catch (err: any) {
			setNotice({ message: 'Error: ' + err.message, variant: 'error' });
		} finally {
			setEditLoading(false);
		}
	};

	const handleCancelEdit = () => {
		setEditing(false);
		setEditMethod(method.toLowerCase());
		setEditBankAccount(bankAccountNumber);
		setEditBankName(bankName);
		setEditAccountHolder(accountHolder);
		setEditDocType(documentType);
		setEditDocNumber(documentNumber);
	};

	const statusBadge = (
		<span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[status] || 'bg-neutral-500/10 text-neutral-400'}`}>
			{status}
		</span>
	);

	const isCompleted = status === 'completed';
	const effectiveBcvRate = isCompleted ? rowBcvRate : (liveBcvRate ?? rowBcvRate);
	const amountBs = netAmount != null && effectiveBcvRate > 1 ? Number(netAmount) * effectiveBcvRate : null;

	const fields: { label: string; value: React.ReactNode; copyText?: string }[] = [
		{ label: 'ID Payout', value: payoutId, copyText: payoutId },
		{ label: 'Estado', value: statusBadge, copyText: status },
		{ label: 'Email del Aliado', value: merchantEmail, copyText: merchantEmail },
		...(accountHolder ? [{ label: 'Titular', value: accountHolder, copyText: accountHolder }] : []),
		...(documentType ? [{ label: 'Tipo Documento', value: documentType, copyText: documentType }] : []),
		...(documentNumber ? [{ label: 'Número Documento', value: documentNumber, copyText: documentNumber }] : []),
		...(bankAccountNumber ? [{ label: 'Número de Cuenta', value: bankAccountNumber, copyText: bankAccountNumber }] : []),
		...(bankName ? [{ label: 'Banco', value: bankName, copyText: bankName }] : []),
		{ label: 'País', value: payoutCountry, copyText: payoutCountry },
		{ label: 'Monto Neto', value: netAmount != null ? `${currency} ${netAmount}` : '-', copyText: netAmount != null ? String(netAmount) : '' },
		...(amountBs != null
			? [
					{
						label: isCompleted ? 'Monto Bs' : 'Monto Bs (est.)',
						value: `Bs ${amountBs.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
						copyText: amountBs.toFixed(2),
					},
					{
						label: isCompleted ? 'Tasa BCV' : 'Tasa BCV (actual)',
						value: effectiveBcvRate.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
						copyText: effectiveBcvRate.toFixed(2),
					},
				]
			: []),
		{ label: 'Método', value: method || '-', copyText: method },
		{ label: 'Fecha de Venta', value: settlementDate || '-', copyText: settlementDate },
		{ label: 'Fecha de Creación', value: createdAt || '-', copyText: createdAt },
		{ label: 'Fecha de Pago Efectivo', value: effectivePaymentDate || '-', copyText: effectivePaymentDate },
		{ label: 'Referencia', value: transferRef || '-', copyText: String(transferRef || '') },
	];

	const handleCopyAll = () => {
		const lines: string[] = [];
		if (accountHolder) lines.push(`Titular: ${accountHolder}`);
		if (documentType || documentNumber) lines.push(`Identificación: ${documentType ? documentType + ' ' : ''}${documentNumber}`);
		if (bankAccountNumber) lines.push(`Número de Cuenta: ${bankAccountNumber}`);
		if (bankName) lines.push(`Banco: ${bankName}`);
		if (merchantEmail) lines.push(`Email: ${merchantEmail}`);
		if (netAmount != null) lines.push(`Monto: ${currency} ${netAmount}`);
		if (amountBs != null) lines.push(`Monto Bs: ${amountBs.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
		if (amountBs != null) lines.push(`Tasa BCV: ${effectiveBcvRate.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
		if (method) lines.push(`Método: ${method}`);
		if (settlementDate) lines.push(`Fecha: ${settlementDate}`);
		if (transferRef) lines.push(`Referencia: ${transferRef}`);
		copyToClipboard(lines.join('\n'), '_all');
	};

	return (
		<Dialog title="Detalle del Payout" onClose={onClose} className="max-w-2xl" noContentWrapper>
			{showRetryConfirm && (
				<ConfirmDialog
					title="Reintentar Payout"
					message={`¿Está seguro que desea reintentar el payout ${payoutId.slice(0, 8)}...?`}
					onConfirm={handleRetry}
					onClose={() => setShowRetryConfirm(false)}
				/>
			)}

			{showCancelConfirm && (
				<ConfirmDialog
					title="Cancelar Payout"
					message={`¿Está seguro que desea cancelar el payout ${payoutId.slice(0, 8)}...?`}
					onConfirm={handleCancel}
					onClose={() => setShowCancelConfirm(false)}
				/>
			)}

			<div className="no-scrollbar space-y-4 overflow-y-auto p-1 leading-normal text-neutral-200">
				{!editing ? (
					<>
						<div className="divide-y divide-neutral-800">
							{fields.map((field) => (
								<div
									key={field.label}
									className="group flex cursor-pointer items-center justify-between gap-4 py-2.5 transition-colors hover:bg-neutral-800/30"
									onClick={() => field.copyText && copyToClipboard(field.copyText, field.label)}
								>
									<span className="text-sm text-neutral-400">{field.label}</span>
									<div className="flex items-center gap-2">
										<span className="text-sm font-medium text-neutral-100">{field.value}</span>
										{field.copyText && (
											<span className="shrink-0 text-neutral-600 transition-colors group-hover:text-neutral-400">
												{copiedField === field.label ? (
													<Check className="size-3.5 text-emerald-400" />
												) : (
													<Copy className="size-3.5" />
												)}
											</span>
										)}
									</div>
								</div>
							))}
						</div>

						<button
							onClick={handleCopyAll}
							className="flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-800 py-2 text-sm text-neutral-400 transition-colors hover:border-neutral-700 hover:text-neutral-200"
						>
							{copiedField === '_all' ? (
								<>
									<Check className="size-3.5 text-emerald-400" />
									Copiado
								</>
							) : (
								<>
									<Copy className="size-3.5" />
									Copiar todo
								</>
							)}
						</button>
					</>
				) : (
					<div className="space-y-3">
						<Select
							options={payoutMethodOptions}
							defaultValue={editMethod}
							onSelectedChange={(value) => setEditMethod(String(value))}
							floatingLabel="Método de pago"
						/>
						<Input
							floatingLabel="Titular de la cuenta"
							placeholder="Nombre del titular"
							value={editAccountHolder}
							onChange={(value) => setEditAccountHolder(value)}
							containerClassName="w-full"
						/>
						<div className="flex gap-3">
							<Select
								options={docTypeOptions}
								defaultValue={editDocType}
								onSelectedChange={(value) => setEditDocType(String(value))}
								floatingLabel="Tipo de documento"
								containerClassName="w-1/3"
							/>
							<Input
								floatingLabel="Número de identificación"
								placeholder="Número de identificación"
								value={editDocNumber}
								onChange={(value) => setEditDocNumber(value)}
								allowedChars="numbers_only"
								containerClassName="w-2/3"
							/>
						</div>
						<Select
							options={banks.map((b) => ({ label: b, value: b }))}
							defaultValue={editBankName || undefined}
							onSelectedChange={(value) => setEditBankName(typeof value === 'string' ? value.trim() : '')}
							floatingLabel="Banco"
							placeholder="Seleccionar banco"
						/>
						<Input
							allowedChars="numbers_only"
							floatingLabel="Número de cuenta"
							placeholder="Ingresar número"
							value={editBankAccount}
							Icon={<Hash className="size-4" />}
							validation={(value) => validate_bank_account_number(value, country, editBankName)}
							onChange={(value) => setEditBankAccount(value.trim())}
							interpolation={BANK_ACCOUNT_INTERPOLATION[countryKey]}
							containerClassName="w-full"
						/>
					</div>
				)}

				{errorMessage && (
					<Notice message={String(errorMessage)} variant="error" />
				)}

				{notice && <Notice message={notice.message} variant={notice.variant} />}

				{!editing && canCancel && (
					<div className="space-y-2">
						<label className="text-sm font-medium text-neutral-200">Razón de cancelación</label>
						<textarea
							placeholder="Ingrese la razón..."
							value={cancelReason}
							onChange={(e) => setCancelReason(e.target.value)}
							className="w-full resize-none rounded-lg border border-neutral-700 bg-neutral-800/50 px-3 py-2 text-sm text-neutral-200 placeholder-neutral-500 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
							rows={2}
						/>
					</div>
				)}

				<div className="flex justify-end gap-3 border-t border-neutral-800 pt-4">
					{editing ? (
						<>
							<Button variant="secondary" className="w-fit" onClick={handleCancelEdit} disabled={editLoading}>
								<X className="h-4 w-4" />
								Cancelar
							</Button>
							<Button variant="primary" className="w-fit" onClick={handleSaveEdit} loading={editLoading}>
								<Save className="h-4 w-4" />
								Guardar
							</Button>
						</>
					) : (
						<>
							{canEdit && (
								<Button variant="secondary" className="w-fit" onClick={() => setEditing(true)}>
									<Pencil className="h-4 w-4" />
									Editar
								</Button>
							)}
							{canRetry && (
								<Button variant="primary" className="w-fit" onClick={() => setShowRetryConfirm(true)}>
									<RefreshCw className="h-4 w-4" />
									Reintentar
								</Button>
							)}
							{canCancel && (
								<Button
									variant="secondary"
									className="w-fit"
									onClick={() => setShowCancelConfirm(true)}
									disabled={!cancelReason.trim()}
								>
									<XCircle className="h-4 w-4" />
									Cancelar Payout
								</Button>
							)}
						</>
					)}
				</div>
			</div>
		</Dialog>
	);
}
